import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

import {
  formatSubmittedValue,
  submittedFormRows,
  type ProcessFormSchemaLike,
} from '../../pages/WorkerPortal/processFormContract';
import ProcessFormResourceValue, {
  isProcessResourceKind,
  type ProcessAttachmentMeta,
} from '../../pages/WorkerPortal/ProcessFormResourceValue';
import { processFormFieldKind } from '../../pages/WorkerPortal/processFormContract';

/**
 * A submitted application, rendered as the FORM it is.
 *
 * Owner, twice on 2026-09-09: *"Submitted data 其实可以理解就是填了一个表格 form"* and *"web端的
 * Submitted application中表格应该是form的表格ui显示"* — plus *"对比worker portal和admin portal的设计，
 * 要一致，要优雅！！包括手机的"*, naming the WORKER WEB PORTAL as the reference of the three.
 *
 * WHY A COMPONENT AND NOT A STYLE GUIDE. The census that prompted this found five surfaces rendering
 * one thing five ways: four different value formatters, three label-resolution strategies, three row
 * layouts (`spacing 0.75` / `1` / a CSS grid) and three names. None of that was carelessness — the
 * value lived at each call site, so every new surface had to re-decide it, and they decided differently
 * (svdy-ui §17). The fix is that no call site decides any of it any more: this component owns the label
 * column, the row rhythm, the formatter and the title.
 *
 * WHAT MAKES IT READ AS A FORM rather than a stack of pairs, taken from the reference surface:
 *   - one LABEL COLUMN at a fixed width, so every value starts on the same axis (svdy-ui §15);
 *   - one ROW RHYTHM for every row, whatever the row contains;
 *   - values that are RESOURCES (a worker, a department, a location, a file) render through the shared
 *     resource renderer, so a bold primary line with a muted meta line beneath is the same everywhere;
 *   - NO row separators. The label column is what separates a row from its neighbour here; a rule would
 *     be a second separator doing the same job. The PHONE keeps its separators for the opposite reason
 *     — its labels sit above their values, so nothing else divides the rows.
 *   - one column below `sm`, because a 140px label column does not fit a narrow window.
 */

/**
 * The label column, and the ONE place its width is written.
 *
 * 140 is the reference surface's value and was already spelled at four call sites. Exported so the
 * timeline card on the instance page can share the axis: two cards on one page whose label columns
 * differ by a few pixels is the misalignment §15 is about.
 */
export const SUBMITTED_LABEL_WIDTH = 140;

/**
 * One rhythm for every row, as PADDING rather than as a gap.
 *
 * It was `Stack spacing={1.25}` — whitespace between rows and nothing else. Padding instead, because the
 * rule below has to sit at the row BOUNDARY with equal air above and below it; a gap would put the line
 * against one row's text and away from the next.
 */
const ROW_PAD_Y = 1;

export interface SubmittedApplicationProps {
  /** The submitted values, keyed by field id. Null for an instance started without a request. */
  formData: unknown;
  /** The parent definition's form schema, which turns a field id into a label. */
  formSchema: ProcessFormSchemaLike | null | undefined;
  attachments?: ProcessAttachmentMeta[] | null;
  /** For a `currency_field` that does not carry its own currency. */
  organizationCurrency?: string | null;
  onDownload?: (attachment: ProcessAttachmentMeta) => Promise<void>;
  t: (key: string, options?: Record<string, unknown>) => string;
  /** Rendered above the rows. Omitted where the caller already has a heading. */
  showTitle?: boolean;
}

const SubmittedApplication: React.FC<SubmittedApplicationProps> = ({
  formData, formSchema, attachments, organizationCurrency, onDownload, t, showTitle = true,
}) => {
  const rows = submittedFormRows(formData, formSchema, t as (key: string) => string);
  // Absent rather than empty: an instance started without a request has no application, and an empty
  // card claiming otherwise is worse than no card.
  if (rows.length === 0) return null;

  return (
    <Box sx={{ width: '100%' }}>
      {showTitle && (
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          {t('processes:submittedApplication.title')}
        </Typography>
      )}
      {/* A RULED FORM, not a stack of pairs (owner, 2026-09-10: *"Submitted application 我建议还是做个 form
          的横线的表格样式，这样非常契合。因为手机就做了这个表格的横线"*).
          WHY IT IS THE RIGHT DEVICE AND NOT DECORATION: the label column is fixed at 140px and the value
          column takes the rest, so on a detail page a short value ("5 days") sits a long way from its own
          label with nothing between them. A rule per row is the classic fix for exactly that — it is why
          invoices, bank statements and legal forms have them — and this app's own §13 language already uses
          hairline `divider` borders for tabular data.
          NO RULE ON THE LAST ROW. The phone's version rules every row including the last, which leaves a
          line hanging under the final value with nothing below it; the reference is copied, its dangling
          edge is not. */}
      <Stack spacing={0}>
        {rows.map(({ key, label, field, value }, index) => (
          <Box
            key={key}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: `${SUBMITTED_LABEL_WIDTH}px minmax(0, 1fr)` },
              columnGap: 1.5,
              rowGap: 0.25,
              alignItems: 'start',
              py: ROW_PAD_Y,
              ...(index < rows.length - 1
                ? { borderBottom: '1px solid', borderColor: 'divider' }
                : null),
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {label}
            </Typography>
            {/* `minWidth: 0` so a 200-character address wraps inside its column instead of widening
                the grid and pushing the label column off the shared axis. */}
            <Box sx={{ minWidth: 0 }}>
              {isProcessResourceKind(processFormFieldKind(field?.type)) ? (
                <ProcessFormResourceValue
                  fieldId={key}
                  fieldType={field?.type}
                  value={value}
                  attachments={attachments}
                  onDownload={onDownload}
                  t={t}
                />
              ) : (
                <Typography variant="body2">
                  {formatSubmittedValue(value, field, formSchema, organizationCurrency, t)}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default SubmittedApplication;
